import { useUpdateBooking } from '@/queries/use-bookings';
import type { Booking, UpdateBookingDto } from '@/entities/bookings/booking.entity';
import { filterOutOfRangeAllocations } from '@/components/booking-timeline/utils/filter-out-of-range-allocations';
import { ref, watch, type WatchSource } from 'vue';
import { getContractTotal } from '@/utils/pricing.utils';

export type PricePreview = {
  transferPrice: number | null;
  totalPrice: number | null;
  isLoading: boolean;
};

type UseTransferPricePreviewArgs = {
  booking: Booking;
  watchSource: WatchSource<unknown> | WatchSource<unknown>[];
  shouldPreview: () => boolean;
  buildBookingDto: () => UpdateBookingDto;
  getTransferPrice: (booking: Booking) => number | null;
  watchDeep?: boolean;
};

export const useTransferPricePreview = ({
  booking,
  watchSource,
  shouldPreview,
  buildBookingDto,
  getTransferPrice,
  watchDeep = false,
}: UseTransferPricePreviewArgs) => {
  const { mutateAsync: updateBooking } = useUpdateBooking({ silent: true });

  const previewTransferPrice = ref<number | null>(null);
  const previewTotalPrice = ref<number | null>(null);
  const isLoadingTransferPrice = ref(false);
  let transferPriceRequestId = 0;

  watch(
    watchSource,
    async () => {
      const requestId = ++transferPriceRequestId;

      if (!shouldPreview()) {
        previewTransferPrice.value = null;
        previewTotalPrice.value = null;
        isLoadingTransferPrice.value = false;
        return;
      }

      isLoadingTransferPrice.value = true;

      try {
        const updatedBooking = await updateBooking({
          id: booking.id,
          booking: {
            ...filterOutOfRangeAllocations(buildBookingDto()),
            dryRun: true,
          },
        });

        if (requestId !== transferPriceRequestId) {
          return;
        }

        previewTransferPrice.value = getTransferPrice(updatedBooking);
        previewTotalPrice.value = getContractTotal(updatedBooking.priceCalculation);
      } catch {
        if (requestId !== transferPriceRequestId) {
          return;
        }

        previewTransferPrice.value = null;
        previewTotalPrice.value = null;
      } finally {
        if (requestId === transferPriceRequestId) {
          isLoadingTransferPrice.value = false;
        }
      }
    },
    { immediate: true, deep: watchDeep },
  );

  return {
    previewTransferPrice,
    previewTotalPrice,
    isLoadingTransferPrice,
  };
};
